import { useState } from "react"
import {
  Home,
  Award,
  BookOpen,
  LayoutDashboard,
  User,
  ShieldCheck,
  LogOut,
  Settings,
  Rocket,
  Users,
  Trophy,
  MapPin,
  LifeBuoy,
  Keyboard,
  ChevronDown,
} from "lucide-react"
import { redirectToSso } from "@alascorporation/api-client"
import { useAuthStore, isStaff } from "../../stores/authStore"
import { OPEN_SHORTCUTS_EVENT } from "../../hooks/useKeyboardShortcuts"
import { useCccdEvents, type CccdSidebarEvent } from "../../hooks/useCccdEvents"
import { ssoConfig } from "../../lib/sso"
import { Link, useNavigate, useLocation } from "react-router-dom"
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarHeader,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarGroup, SidebarGroupLabel, SidebarGroupContent,
} from "@/components/ui/sidebar"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"

// Un evento bajo "Eventos". El icono distingue nacional (trofeo) de regional (marcador), así que
// no hacen falta rótulos de sección. El nombre es una fila legible y, debajo, dos accesos
// compactos en la misma línea: "Categoría" (solo si el usuario tiene categoría en este evento,
// o es staff) y "Equipos". Con el sidebar contraído, cada evento es un icono con su nombre en
// el tooltip que lleva a su categoría o, si no la tiene, a sus equipos.
function EventMenuBlock({
  event,
  scope,
  isStaffUser,
  isActive,
}: {
  event: CccdSidebarEvent
  scope: "national" | "regional"
  isStaffUser: boolean
  isActive: (url: string) => boolean
}) {
  const categoriesUrl = `/cccd/${event.id}`
  const competitorsUrl = `/cccd/competidores/${event.id}`
  const showCategorias = isStaffUser || event.categorySlug !== null
  const ScopeIcon = scope === "national" ? Trophy : MapPin
  const scopeLabel = scope === "national" ? "Evento nacional" : "Evento regional"
  const eventActive = isActive(categoriesUrl) || isActive(competitorsUrl)

  const pill = (active: boolean) =>
    cn(
      "inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-sidebar-ring [@media(pointer:coarse)]:min-h-11",
      active ? "bg-primary/10 text-primary" : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-white"
    )

  return (
    <SidebarMenuItem className="border-t border-sidebar-border/40 pt-1 first:border-t-0 first:pt-0 group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:pt-0">
      {/* Sidebar expandido */}
      <div className="group-data-[collapsible=icon]:hidden">
        <div className="flex items-center gap-2 px-2 pb-1 pt-1.5">
          <ScopeIcon
            aria-hidden="true"
            className={cn("h-4 w-4 shrink-0", eventActive ? "text-primary" : "text-sidebar-foreground/60")}
          />
          <span className={cn("truncate text-sm font-semibold", eventActive ? "text-white" : "text-sidebar-foreground")}>
            {event.name}
          </span>
          <span className="sr-only">{scopeLabel}</span>
        </div>
        <div className="flex flex-wrap gap-1 pb-1.5 pl-8 pr-2">
          {showCategorias && (
            <Link to={categoriesUrl} aria-current={isActive(categoriesUrl) ? "page" : undefined} className={pill(isActive(categoriesUrl))}>
              <Rocket aria-hidden="true" className="h-3.5 w-3.5" />
              {isStaffUser ? "Categorías" : "Tu categoría"}
            </Link>
          )}
          <Link to={competitorsUrl} aria-current={isActive(competitorsUrl) ? "page" : undefined} className={pill(isActive(competitorsUrl))}>
            <Users aria-hidden="true" className="h-3.5 w-3.5" />
            Equipos
          </Link>
        </div>
      </div>

      {/* Sidebar contraído: un icono por evento */}
      <div className="hidden group-data-[collapsible=icon]:block">
        <SidebarMenuButton
          render={<Link to={showCategorias ? categoriesUrl : competitorsUrl} />}
          tooltip={`${event.name} (${scopeLabel.toLowerCase()})`}
          className={cn(
            "rounded-lg",
            eventActive ? "bg-primary/10 text-primary" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-white"
          )}
        >
          <ScopeIcon className="h-4 w-4" />
          <span>{event.name}</span>
        </SidebarMenuButton>
      </div>
    </SidebarMenuItem>
  )
}

// Con más de 3 eventos el grupo "Eventos" se puede plegar para no saturar el sidebar. La
// preferencia se recuerda en este navegador; sin almacenamiento, arranca desplegado.
const EVENTS_COLLAPSE_THRESHOLD = 3
const EVENTS_OPEN_KEY = "sidebar-events-open"

function readEventsOpen() {
  try {
    return localStorage.getItem(EVENTS_OPEN_KEY) !== "0"
  } catch {
    return true
  }
}

export default function AppSidebar() {
  const { user, group, sessionType, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { nationalEvents, regionalEvents } = useCccdEvents()
  const hasAnyCccdEvent = nationalEvents.length > 0 || regionalEvents.length > 0
  const eventCount = nationalEvents.length + regionalEvents.length
  const canCollapseEvents = eventCount > EVENTS_COLLAPSE_THRESHOLD
  const [eventsOpen, setEventsOpen] = useState(readEventsOpen)
  const eventsHidden = canCollapseEvents && !eventsOpen
  const toggleEvents = () => {
    const next = !eventsOpen
    setEventsOpen(next)
    try {
      localStorage.setItem(EVENTS_OPEN_KEY, next ? "1" : "0")
    } catch {
      // sin almacenamiento: el pliegue solo dura la sesión
    }
  }

  const handleLogout = () => {
    logout()
    navigate("/")
  }

  const isActive = (url: string) => location.pathname === url

  // El catálogo (/courses) cubre /courses/:slug en el resaltado. El catálogo es solo para
  // visitantes sin sesión: con sesión no hay ítem de catálogo y el menú es "Mi cuenta".
  const isCatalogActive = location.pathname === "/courses" || location.pathname.startsWith("/courses/")
  const navItems = user
    ? []
    : [
        { title: "Inicio", icon: Home, url: "/" },
        { title: "Cursos", icon: BookOpen, url: "/courses" },
      ]
  const accountItems = [
    { title: "Inicio", icon: LayoutDashboard, url: "/dashboard" },
    { title: "Mis cursos", icon: BookOpen, url: "/mis-cursos" },
    { title: "Certificados", icon: Award, url: "/certificados" },
    { title: "Ayuda", icon: LifeBuoy, url: "/ayuda" },
  ]

  return (
    <Sidebar variant="sidebar" collapsible="icon">
      {/* Logo */}
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <div className="flex items-center justify-between gap-2.5 px-1">
          <Link
            to={user || group ? "/dashboard" : "/"}
            className="flex items-center gap-2.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
          >
            <img 
              src="https://satelab.org/Aviaci%C3%B3n_And_Space_Academy.webp" 
              alt="SateLab Academy" 
              className="h-8 w-auto object-contain"
            />
            <span className="text-base font-bold tracking-tight text-white group-data-[collapsible=icon]:hidden">
              SateLab
            </span>
          </Link>
        </div>
      </SidebarHeader>

      <SidebarContent className="py-2">
        {/* Nav — not for group sessions: "Inicio"/"Cursos" is the public
            marketing nav, redundant with the group's own "Grupos"/"Eventos"
            sections below. */}
        {sessionType !== "group" && navItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      render={<Link to={item.url} />}
                      tooltip={item.title}
                      className={cn(
                        "rounded-lg text-sm font-medium transition-[width,height,padding,background-color,color] duration-(--dur-fast) [&>svg]:transition-transform [&>svg]:duration-(--dur-fast) hover:[&>svg]:translate-x-0.5 motion-reduce:hover:[&>svg]:translate-x-0",
                        (item.url === "/courses" ? isCatalogActive : isActive(item.url))
                          ? "bg-primary/10 text-primary"
                          : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-white"
                      )}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {user && (
          <>
            <SidebarGroup className="mt-2">
              <SidebarGroupLabel className="px-3 pb-1 text-micro font-semibold uppercase tracking-wider text-sidebar-foreground/70">
                Mi cuenta
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {accountItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        render={<Link to={item.url} />}
                        tooltip={item.title}
                        className={cn(
                          "rounded-lg text-sm font-medium transition-[width,height,padding,background-color,color] duration-(--dur-fast) [&>svg]:transition-transform [&>svg]:duration-(--dur-fast) hover:[&>svg]:translate-x-0.5 motion-reduce:hover:[&>svg]:translate-x-0",
                          isActive(item.url)
                            ? "bg-primary/10 text-primary"
                            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-white"
                        )}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {hasAnyCccdEvent && (
          <SidebarGroup className="mt-2">
            <SidebarGroupLabel className="px-3 pb-1 text-micro font-semibold uppercase tracking-wider text-sidebar-foreground/70">
              {canCollapseEvents ? (
                <button
                  type="button"
                  aria-expanded={eventsOpen}
                  aria-controls="sidebar-eventos"
                  onClick={toggleEvents}
                  className="flex w-full items-center justify-between rounded-md uppercase outline-none transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-sidebar-ring [@media(pointer:coarse)]:min-h-11"
                >
                  <span>
                    Eventos <span className="tabular-nums">({eventCount})</span>
                  </span>
                  <ChevronDown
                    aria-hidden="true"
                    className={cn("h-3.5 w-3.5 transition-transform motion-reduce:transition-none", !eventsOpen && "-rotate-90")}
                  />
                </button>
              ) : (
                "Eventos"
              )}
            </SidebarGroupLabel>
            {/* Con el sidebar contraído a iconos no hay botón para desplegar: los eventos siguen visibles. */}
            <SidebarGroupContent id="sidebar-eventos" className={cn(eventsHidden && "hidden group-data-[collapsible=icon]:block")}>
              <SidebarMenu>
                {nationalEvents.map((event) => (
                  <EventMenuBlock key={event.id} event={event} scope="national" isStaffUser={isStaff(user)} isActive={isActive} />
                ))}
                {regionalEvents.map((event) => (
                  <EventMenuBlock key={event.id} event={event} scope="regional" isStaffUser={isStaff(user)} isActive={isActive} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {sessionType === "group" && group && (
          <SidebarGroup className="mt-2">
            <SidebarGroupLabel className="px-3 pb-1 text-micro font-semibold uppercase tracking-wider text-sidebar-foreground/70">
              Grupos
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    render={<Link to="/dashboard" />}
                    tooltip="Aprendizaje"
                    className={cn(
                      "rounded-lg text-sm font-medium transition-[width,height,padding,background-color,color] duration-(--dur-fast) [&>svg]:transition-transform [&>svg]:duration-(--dur-fast) hover:[&>svg]:translate-x-0.5 motion-reduce:hover:[&>svg]:translate-x-0",
                      isActive("/dashboard")
                        ? "bg-primary/10 text-primary"
                        : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-white"
                    )}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>Aprendizaje</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    render={<Link to="/grupo/certificados" />}
                    tooltip="Certificados"
                    className={cn(
                      "rounded-lg text-sm font-medium transition-[width,height,padding,background-color,color] duration-(--dur-fast) [&>svg]:transition-transform [&>svg]:duration-(--dur-fast) hover:[&>svg]:translate-x-0.5 motion-reduce:hover:[&>svg]:translate-x-0",
                      isActive("/grupo/certificados")
                        ? "bg-primary/10 text-primary"
                        : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-white"
                    )}
                  >
                    <ShieldCheck className="h-4 w-4" />
                    <span>Certificados</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* Footer — user dropdown */}
      <SidebarFooter className="border-t border-sidebar-border p-3">
        {user ? (
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <SidebarMenuButton
                      size="lg"
                      className="rounded-lg data-[state=open]:bg-sidebar-accent"
                    />
                  }
                >
                  <Avatar className="h-8 w-8 rounded-md">
                    {user.avatar_url && <AvatarImage src={user.avatar_url} alt="" />}
                    <AvatarFallback className="rounded-md bg-primary/20 text-xs font-bold text-primary">
                      {user.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="truncate font-semibold text-white">{user.name}</span>
                    <span className="truncate text-xs text-sidebar-foreground/75">{user.email}</span>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-56 rounded-lg border-sidebar-border bg-sidebar p-1"
                  side="right"
                  align="end"
                  sideOffset={4}
                >
                  <DropdownMenuItem render={<a href={`${ssoConfig.hubUrl}/cuenta/perfil-estudiante`} target="_blank" rel="noreferrer" />}>
                    <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-sidebar-foreground">
                      <User className="h-4 w-4" />
                      <span>Mi perfil</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem render={<a href={`${ssoConfig.hubUrl}/cuenta/seguridad`} target="_blank" rel="noreferrer" />}>
                    <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-sidebar-foreground">
                      <Settings className="h-4 w-4" />
                      <span>Configuración</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => window.dispatchEvent(new Event(OPEN_SHORTCUTS_EVENT))}>
                    <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-sidebar-foreground">
                      <Keyboard className="h-4 w-4" />
                      <span>Atajos de teclado</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-2 py-1.5 text-sm text-destructive focus:bg-destructive/10 focus:text-destructive"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Cerrar sesión</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : sessionType === "group" && group ? (
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="grid flex-1 px-2 py-1.5 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold text-white">{group.name}</span>
                <span className="truncate text-xs text-sidebar-foreground/75">Cuenta grupal</span>
              </div>
              <SidebarMenuButton
                onClick={handleLogout}
                tooltip="Cerrar sesión"
                className="rounded-lg text-sm text-destructive hover:bg-destructive/10"
              >
                <LogOut className="h-4 w-4" />
                <span>Cerrar sesión</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => redirectToSso(ssoConfig, "/dashboard")} tooltip="Ingresar" className="rounded-lg text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-white">
                <LogOut className="h-4 w-4 rotate-180" />
                <span>Ingresar</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarFooter>
    </Sidebar>
  )
}
